import { ReactComponent as plusIcon } from '@epam/assets/icons/common/action-add-12.svg';
import { Button, FlexCell, FlexRow, Panel } from '@epam/promo';
import { Outlet, useNavigate } from 'react-router-dom';

import { BlankBlock } from 'components/common';
import CardFilters from 'components/common/CardFilters/CardFilters';
import { CardList } from 'components/recognitions';
import { useRecognitionSwitcher } from 'hooks';
import { useAppLocation } from 'hooks/helpers';
import { useMemo, useRef } from 'react';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { resetDraft, useRecognitionStore } from 'store/recognition';
import css from './RecognitionView.module.scss';
import { RecognitionPreview } from 'components/common/PreviewWrapper';

export const RecognitionView = () => {
  const navigate = useNavigate();
  const switchTask = useRecognitionSwitcher();
  const { draft, recognitions } = useRecognitionStore();
  const { isCreate, isNotFound } = useAppLocation(recognitionRoutes);
  const panelRef = useRef<HTMLDivElement>(null);

  const items = isCreate ? [draft, ...recognitions] : [...recognitions];

  const form = useMemo(() => {
    if (draft.id || isCreate) {
      return <Outlet />;
    }

    const header = isNotFound
      ? 'Recognition is not found!'
      : 'Nothing to display yet';
    const message = isNotFound
      ? 'Please create another recognition'
      : 'Please create your first recognition';

    return <BlankBlock header={header} message={message} />;
  }, [draft.id, isCreate, isNotFound]);

  return (
    <FlexRow cx={css.page__container}>
      <FlexCell ref={panelRef} cx={css['page__left-panel']} grow={1}>
        <Button
          caption="Add a new recognition"
          color="blue"
          cx={css.page__btn}
          icon={plusIcon}
          rawProps={{ 'data-testid': 'add-new-recognition-btn' }}
          onClick={() =>
            switchTask({
              onSuccess: () => {
                resetDraft();
                navigate(recognitionRoutes.create);
              },
              onAbort: () => {
                resetDraft();
                navigate(recognitionRoutes.create);
              },
            })
          }
        />
        {(recognitions?.length || isCreate) && (
          <CardFilters
            CardList={CardList}
            items={items}
            panelHeight={panelRef.current?.clientHeight ?? 0}>
            <CardList items={items} />
          </CardFilters>
        )}
      </FlexCell>
      <FlexCell cx={css['page__central-panel']} grow={2.5}>
        <Panel shadow cx={css.page__panel}>
          {form}
        </Panel>
      </FlexCell>
      <FlexCell cx={css['page__right-panel']} grow={1}>
        <RecognitionPreview />
      </FlexCell>
    </FlexRow>
  );
};
